/**
 * Timezone-safe date string utilities
 *
 * All functions work with DATE strings (YYYY-MM-DD) without timezone conversion.
 * Never use new Date() for month/date comparisons - it introduces timezone bugs.
 */

/**
 * Get date string for the first day of a month
 * @param year - Full year (e.g., 2026)
 * @param month - Month number 1-12
 * @returns Date string YYYY-MM-01
 */
export function getMonthStart(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`
}

/**
 * Get date string for the last day of a month
 * @param year - Full year (e.g., 2026)
 * @param month - Month number 1-12
 * @returns Date string YYYY-MM-DD (last day)
 */
export function getMonthEnd(year: number, month: number): string {
  // Create date at noon UTC to avoid timezone issues
  const date = new Date(Date.UTC(year, month, 0, 12, 0, 0))
  const lastDay = date.getUTCDate()
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

/**
 * Check if a date string is within a specific month
 * @param dateString - Date string in YYYY-MM-DD format
 * @param year - Full year
 * @param month - Month number 1-12
 * @returns true if date is in the specified month
 */
export function isDateInMonth(dateString: string, year: number, month: number): boolean {
  const monthStart = getMonthStart(year, month)
  const monthEnd = getMonthEnd(year, month)
  return dateString >= monthStart && dateString <= monthEnd
}

/**
 * Check if a date string is within a specific year
 * @param dateString - Date string in YYYY-MM-DD format
 * @param year - Full year
 * @returns true if date is in the specified year
 */
export function isDateInYear(dateString: string, year: number): boolean {
  const yearStart = `${year}-01-01`
  const yearEnd = `${year}-12-31`
  return dateString >= yearStart && dateString <= yearEnd
}

/**
 * Get current month and year
 * @returns Object with year and month (1-12)
 */
export function getCurrentMonthYear(): { year: number; month: number } {
  const now = new Date()
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1
  }
}

/**
 * Compare two date strings (YYYY-MM-DD format)
 * @returns true if date1 < date2
 */
export function isDateBefore(date1: string, date2: string): boolean {
  return date1 < date2
}

/**
 * Compare two date strings for equality or before
 * @returns true if date1 <= date2
 */
export function isDateBeforeOrEqual(date1: string, date2: string): boolean {
  return date1 <= date2
}

/**
 * Compare two date strings (YYYY-MM-DD format)
 * @returns true if date1 > date2
 */
export function isDateAfter(date1: string, date2: string): boolean {
  return date1 > date2
}

/**
 * Compare two date strings for equality or after
 * @returns true if date1 >= date2
 */
export function isDateAfterOrEqual(date1: string, date2: string): boolean {
  return date1 >= date2
}
